import { charLimitMessage } from "@/lib/utils";
import React from "react";
import { useEffect, useState } from "react";
import {
  getDrugBySearch,
  setselectedDrugs,
  setDrugSummary,
} from "@/dispatchers/nmsDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import {
  useMedicationFormState,
  handleSubmit,
  therapyLevelOptions,
  MedicationForm,
  MedicationEditMode,
  useMedicationDose,
  therapyLevelNumberToString,
  MEDICATION_TEXT_MAX_LENGTH,
  MEDICATION_NOTES_MAX_LENGTH,
  MEDICATION_QUANTITY_MAX_LENGTH,
  toWholeNumberText,
} from "@/lib/medicationsData";
import SearchableDropdown from "@/components/AddPrescriptionItem/SearchableDropdown";
import { therapyLevelMap } from "@/lib/patient/addPatientData";

interface MedicationsModalProps {
  trigger: React.ReactNode;
  onSave: (medication: MedicationForm) => void;
  editMode?: MedicationEditMode;
  isDarkMode?: boolean;
}

export function MedicationsModal({
  trigger,
  onSave,
  editMode,
  isDarkMode = false,
}: MedicationsModalProps) {
  const {
    open,
    setOpen,
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useMedicationFormState(editMode);
  const { handleDose, searchLoading, drugSearch, drugSummary } =
    useMedicationDose();

  const therapyLevel = watch("therapyLevel");
  const isAtLimit = (value: string | undefined | null, max: number) =>
    (value || "").length >= max;

  const resetMedicationForm = () => {
    if (editMode) {
      reset({
        item: editMode.item || "",
        dose: editMode.dose || "",
        quantity: editMode.quantity || "",
        therapyLevel:
          typeof editMode.therapyLevel === "number"
            ? therapyLevelNumberToString[editMode.therapyLevel] || "acute"
            : editMode.therapyLevel,
        notes: editMode.notes || "",
      });
    } else {
      reset({
        item: "",
        dose: "",
        quantity: "",
        therapyLevel: "acute",
        notes: "",
      });
    }
  };

  useEffect(() => {
    resetMedicationForm();
  }, [editMode]);

  const handleDrugSearch = async (keyword: string) => {
    await drugSearch(keyword);
  };

  const handleDoseChange = (field, value) => {
    var dose = handleDose(value);
    setValue(field, dose);
  };
  const onSubmit = (data: MedicationForm) => {
    handleSubmit(data, onSave, setOpen, reset);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) {
          resetMedicationForm();
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={`sm:max-w-[500px] max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {editMode ? "Edit Patient Medication" : "Add Patient Medication"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit(onSubmit)} className="space-y-6">
          {/* Item */}
          <div className="space-y-2">
            <Label
              htmlFor="item"
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Item *
            </Label>

            <SearchableDropdown
              className={`searchable-dropdown ${
                errors.item ? "border-red-500" : ""
              }`}
              searchType="drug"
              placeholder="e.g., Amlodipine 10mg tablets"
              label="drugName"
              id="id"
              selectedVal={watch("item")}
              handleChange={(val: any) => {
                if (val && typeof val === "object") {
                  setValue("item", val?.drugName?.trim() ?? "");
                } else {
                  setValue("item", val);
                }
              }}
              options={Array.isArray(drugSummary) ? drugSummary : []}
              onInputChange={handleDrugSearch}
              loading={searchLoading}
              maxLength={MEDICATION_TEXT_MAX_LENGTH}
            />

            {errors.item && (
              <p className="text-sm text-red-600">{errors.item.message}</p>
            )}
          </div>

          {/* Dose */}
          <div className="space-y-2">
            <Label
              htmlFor="dose"
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Dose *
            </Label>
            <Input
              id="dose"
              type="text"
              placeholder="e.g., Take one daily with food"
              {...register("dose")}
              onChange={(e) => handleDoseChange("dose", e.target.value)}
              maxLength={MEDICATION_TEXT_MAX_LENGTH}
              className="w-full"
            />
            {(errors.dose ||
              isAtLimit(watch("dose"), MEDICATION_TEXT_MAX_LENGTH)) && (
              <p className="text-sm text-red-600">
                {isAtLimit(watch("dose"), MEDICATION_TEXT_MAX_LENGTH)
                  ? charLimitMessage(MEDICATION_TEXT_MAX_LENGTH)
                  : errors.dose?.message}
              </p>
            )}
          </div>

          {/* Quantity and Therapy */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="quantity"
                className="text-sm font-medium text-gray-700"
              >
                Quantity
              </Label>
              <Input
                id="quantity"
                type="text"
                inputMode="numeric"
                placeholder="e.g., 30"
                {...register("quantity")}
                onChange={(e) =>
                  setValue(
                    "quantity",
                    toWholeNumberText(e.target.value, watch("quantity")),
                    {
                      shouldValidate: true,
                    },
                  )
                }
                maxLength={MEDICATION_QUANTITY_MAX_LENGTH}
                className="w-full"
              />
              {errors.quantity && (
                <p className="text-sm text-red-600">
                  {errors.quantity.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Therapy Level *
              </Label>
              <Select
                value={therapyLevel}
                onValueChange={(
                  value: "acute" | "chronic" | "maintenance" | "asneeded",
                ) => setValue("therapyLevel", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {therapyLevelOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label
              htmlFor="notes"
              className="text-sm font-medium text-gray-700"
            >
              Additional Notes
            </Label>
            <Textarea
              id="notes"
              placeholder="Any additional information about this medication..."
              {...register("notes")}
              maxLength={MEDICATION_NOTES_MAX_LENGTH}
              className="w-full"
              rows={3}
            />
            {(errors.notes ||
              isAtLimit(watch("notes"), MEDICATION_NOTES_MAX_LENGTH)) && (
              <p className="text-sm text-red-600">
                {isAtLimit(watch("notes"), MEDICATION_NOTES_MAX_LENGTH)
                  ? charLimitMessage(MEDICATION_NOTES_MAX_LENGTH)
                  : errors.notes?.message}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetMedicationForm();
                setOpen(false);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-slate-900 hover:bg-slate-800"
            >
              {editMode ? "Update Medication" : "Add Medication"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AddMedicationButton({
  onSave,
  editMode,
  trigger,
}: {
  onSave: (medication: MedicationForm) => void;
  editMode?: MedicationEditMode;
  trigger?: React.ReactNode;
}) {
  return (
    <MedicationsModal
      trigger={
        trigger || (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-0 font-normal text-gray-500 hover:text-gray-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Medication
          </Button>
        )
      }
      onSave={onSave}
      editMode={editMode}
    />
  );
}
